/**
 * +field: hero levels and kill XP (docs/design/zones.md "Levelling", Phase 1). Hero level / XP live
 * on `characters[classId].hero` (talentPoints = level). Pure: no clock, no Math.random.
 */
import type { AbilityId, ClassDef, ClassId } from '@keystone/sim';
import { levelMult } from '@keystone/sim';
import { getContentBundle } from '@keystone/content';
import type { CharacterProgress } from './types';

/** Phase 1 level cap: XP stops at this level. */
export const LEVEL_CAP = 10;

const round10 = (x: number): number => Math.round(x / 10) * 10;

/**
 * Curve scale: halved (was 12.8) so an active player doing the main quest reaches 10 in ≈ 30 min.
 * Competent Auto (bots, kills only) then takes ≈ 40 min across the five base classes (harness
 * `grind`); fixed quest/discovery/bounty XP is unchanged, so questing carries a bigger share.
 */
export const XP_CURVE_SCALE = 6.4;

/** XP to go from `level` to `level + 1` (L1 ≈ 270, L5 ≈ 1340, L9 ≈ 3240). Defined past the cap too (display only). */
export function xpToNext(level: number): number {
  const l = Math.max(1, Math.floor(level) || 1);
  return round10(XP_CURVE_SCALE * (10 + 4 * l) * (2 + l));
}

/** Base XP of a kill at even level. */
export const killXpBase = (mobLevel: number): number => 10 + 4 * mobLevel;

/** XP multiplier per level the hero is above the mob (floor KILL_XP_FLOOR). */
export const KILL_XP_PENALTY = 0.15;
export const KILL_XP_FLOOR = 0.1;
/** XP bonus per level the mob is above the hero (up to KILL_XP_BONUS_LEVELS levels). */
export const KILL_XP_BONUS = 0.05;
export const KILL_XP_BONUS_LEVELS = 4;
/** Elite kills pay this many times the XP. */
export const ELITE_XP_MULT = 4;

/**
 * XP for one kill: (10 + 4·mob) × max(0.10, 1 − 0.15·gap) when the hero is above the mob,
 * × (1 + 0.05·min(gap, 4)) when the mob is above the hero; elites × 4. Rounded, at least 1.
 */
export function killXp(mobLevel: number, heroLevel: number, elite = false): number {
  const mob = Math.max(1, Math.floor(mobLevel) || 1);
  const hero = Math.max(1, Math.floor(heroLevel) || 1);
  let mult = 1;
  if (hero > mob) mult = Math.max(KILL_XP_FLOOR, 1 - KILL_XP_PENALTY * (hero - mob));
  else if (mob > hero) mult = 1 + KILL_XP_BONUS * Math.min(mob - hero, KILL_XP_BONUS_LEVELS);
  return Math.max(1, Math.round(killXpBase(mob) * mult * (elite ? ELITE_XP_MULT : 1)));
}

export type LevelColor = 'grey' | 'green' | 'yellow' | 'orange' | 'red';

/** WoW con colour of a mob for a hero (gap = mob − hero): ≥5 red, 3–4 orange, −2..2 yellow, −3..−4 green, ≤−5 grey. */
export function levelColor(mobLevel: number, heroLevel: number): LevelColor {
  const gap = Math.floor(mobLevel) - Math.floor(heroLevel);
  if (gap >= 5) return 'red';
  if (gap >= 3) return 'orange';
  if (gap >= -2) return 'yellow';
  if (gap >= -4) return 'green';
  return 'grey';
}

/**
 * Add XP to a character's hero (mutates `ch`); returns levels gained. Stops at LEVEL_CAP (XP is
 * then 0); talent points = level.
 */
export function addXp(ch: CharacterProgress, xp: number): number {
  const start = ch.hero.level;
  if (ch.hero.level < LEVEL_CAP) ch.hero.xp += Math.max(0, Math.floor(xp) || 0);
  while (ch.hero.level < LEVEL_CAP && ch.hero.xp >= xpToNext(ch.hero.level)) {
    ch.hero.xp -= xpToNext(ch.hero.level);
    ch.hero.level += 1;
  }
  if (ch.hero.level >= LEVEL_CAP) ch.hero.xp = 0;
  ch.hero.talentPoints = ch.hero.level;
  return Math.max(0, ch.hero.level - start);
}

// ---------------------------------------------------------------------------
// Names
// ---------------------------------------------------------------------------

export const HERO_NAME_MIN = 2;
export const HERO_NAME_MAX = 16;

/**
 * A character name, cleaned: trimmed, runs of spaces collapsed, first letter upper-cased. Letters
 * plus single spaces, apostrophes or hyphens between letters; 2–16 characters. null = not allowed.
 */
export function sanitizeHeroName(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const s = raw.normalize('NFC').trim().replace(/\s+/g, ' ');
  if (s.length < HERO_NAME_MIN || s.length > HERO_NAME_MAX) return null;
  if (!/^\p{L}+(?:[ '\-]\p{L}+)*$/u.test(s)) return null;
  return s.charAt(0).toLocaleUpperCase() + s.slice(1);
}

// ---------------------------------------------------------------------------
// Unlocks and level stats
// ---------------------------------------------------------------------------

function baseClass(classId: ClassId): ClassDef | undefined {
  return getContentBundle().classes.find((c) => c.id === classId);
}

/** Level an ability unlocks at (1 = from the start; ClassDef.unlocks, absent = 1). */
export function unlockLevel(classId: ClassId, abilityId: AbilityId, classDef: ClassDef | undefined = baseClass(classId)): number {
  return classDef?.unlocks?.[abilityId] ?? 1;
}

/** Abilities that unlock exactly at `level` (sorted by id). */
export function abilitiesUnlockedAt(classId: ClassId, level: number, classDef: ClassDef | undefined = baseClass(classId)): AbilityId[] {
  return unlockedBetween(classId, level - 1, level, classDef);
}

/** Abilities that unlock above level `from` up to and including `to` (a multi-level ding), by level then id. */
export function unlockedBetween(classId: ClassId, from: number, to: number, classDef: ClassDef | undefined = baseClass(classId)): AbilityId[] {
  const u = classDef?.unlocks ?? {};
  return Object.entries(u)
    .filter(([, l]) => typeof l === 'number' && l > from && l <= to)
    .sort(([a, la], [b, lb]) => (la as number) - (lb as number) || (a < b ? -1 : a > b ? 1 : 0))
    .map(([id]) => id);
}

/** Max HP and power of a hero at `level` (levelMult on the class's base numbers; the level-up banner's gains). */
export function heroLevelStats(classId: ClassId, level: number, classDef: ClassDef | undefined = baseClass(classId)): { maxHp: number; power: number } {
  const m = levelMult(level);
  return { maxHp: Math.round((classDef?.maxHp ?? 0) * m), power: Math.round((classDef?.baseStats.power ?? 0) * m) };
}
